// @ts-nocheck — composed component; types added when touched again later
import React from "react";
// Phase 4 sub-step 10b — explicit primitive import (window publish kept
// for back-compat with bare-name JSX in older code).
import { Button } from "../../legacy/primitives";
// Phase 3b — adopt Drawer for the slide-over chrome + v2 Modal for the
// sound-picker. Phase D — the inner group/title markup migrates to the
// v2 Section primitive so the drawer's structure matches the rest of the
// platform (admin pages, profile, etc.).
import Drawer from "../ui/Drawer";
import V2Modal from "../ui/Modal";
import Section from "../ui/Section";

/* composed/InputsPanel.tsx — assumptions drawer + sound-picker modal
   (Phase 4 sub-step 6). Relocated from src/legacy/inputs-panel.tsx.

   Exports:
     default InputsPanel       — slide-over drawer (basket, ARPU channels,
                                 AE assumptions, SDR funnel, AE roster,
                                 notifications)
     named   SoundPickerModal  — notification sound preset chooser
                                 (memoized)
     named   Assump, AvgBasketInput — small input helpers used by the
                                 drawer; exported for reuse

   Still reads window.{Button,EditableNumber,useEscapeClose,useFocusTrap,
   NOTIF_SOUND_PRESETS,NOTIF_DURATIONS,playNotifSound} at render time —
   all published by legacy primitives.tsx + app.tsx + lib/hooks. Future
   sub-steps replace those with proper ESM imports.

   Behavior preserved byte-for-byte — structural relocation only. */

// === Legacy file header preserved ===
// inputs-panel.jsx — modal/drawer surfaces extracted from app.jsx (Phase B sub-PR 5).
//
// Houses the right-side inputs drawer ("Modello + Scenari") and the sound-picker
// modal, plus the small input helpers used by the drawer (Assump, AvgBasketInput).
// Per ROLLOUT.md §4 sub-PR 5: every modal/drawer that's NOT the generic <Modal>
// primitive (which stays in app.jsx for now). Future modals (login, profile
// setup, marketing inputs, AE assignment) land here too.
//
// Public API:
//   <InputsPanel open onClose lang state setState dynamicArpu />
//   <SoundPickerModal lang notifSound setNotifSound notifDuration setNotifDuration
//                     hasCustomAudio setHasCustomAudio onClose title confirmLabel />
//   <Assump label value onChange prefix? suffix? />        (used by InputsPanel)
//   <AvgBasketInput label value auto dynamicArpu onChange onReset onForce lang />
//
// Loading: <script src="inputs-panel.jsx"> in Forecast.html, AFTER primitives.jsx
// (uses Button, EditableNumber via window) and BEFORE app.jsx. SoundPickerModal
// reads NOTIF_SOUND_PRESETS / NOTIF_DURATIONS / playNotifSound from window —
// app.jsx still owns those (used elsewhere by the notification bell + settings).
// Window assignment for them happens in app.jsx; SoundPickerModal's body only
// resolves them at render time, by which point app.jsx has loaded.

const { useState: _useState_ip, useEffect: _useEffect_ip, useRef: _useRef_ip } = React;

// ─── Small input helpers used by InputsPanel ───────────────────────────────

function Assump({ label, value, onChange, prefix, suffix }) {
  const EditableNumber = window.EditableNumber;
  return (
    <div className="assump">
      <div className="assump__label">{label}</div>
      <div className="assump__val"><EditableNumber value={value} onChange={onChange} prefix={prefix} suffix={suffix} /></div>
    </div>
  );
}

// avgBasket input with dynamic ARPU support. Two modes:
//   - auto (default): the effective value used everywhere is `dynamicArpu` (live from HubSpot
//     deals); the saved `state.avgBasket` is ignored. Badge says "auto · ARPU live".
//   - manuale: forced value (`state.avgBasket`) is used. Badge says "manuale".
// Buttons: "Reset" → back to auto; "Forza" → take the typed draft and lock it as manual.
function AvgBasketInput({ label, value, auto, dynamicArpu, onChange, onReset, onForce, lang }) {
  const Button = window.Button;
  const effective = auto && dynamicArpu ? Math.round(dynamicArpu) : value;
  const [draft, setDraft] = _useState_ip(effective);
  // Sync draft when external effective value changes (only when not editing)
  _useEffect_ip(() => { setDraft(effective); }, [effective]);
  const isDirty = draft !== effective;
  return (
    <div className="assump assump--basket">
      <div className="assump__label">
        {label}
        <span className={`assump__mode ${auto ? "assump__mode--auto" : "assump__mode--manual"}`}>
          {auto
            ? (lang === "it" ? "auto · ARPU live" : "auto · live ARPU")
            : (lang === "it" ? "manuale" : "manual")}
        </span>
      </div>
      <div className="assump__val assump__val--with-actions">
        <input
          type="number"
          className="edit-input assump__input"
          value={draft}
          onChange={(e) => setDraft(Number(e.target.value) || 0)}
          onBlur={() => { if (!auto && draft !== value) onChange(draft); }}
          onKeyDown={(e) => { if (e.key === "Enter") { onForce(draft); e.target.blur(); } }}
        />
        {!auto && (
          <Button variant="ghost" size="sm" tone="success" onClick={onReset}
            title={lang === "it" ? "Reset · usa ARPU live" : "Reset · use live ARPU"}>
            ↺ {lang === "it" ? "Reset" : "Reset"}
          </Button>
        )}
        {(auto || isDirty) && (
          <Button variant="ghost" size="sm" onClick={() => onForce(draft)}
            title={lang === "it" ? "Forza questo valore come manuale" : "Force this value as manual"}>
            🔒 {lang === "it" ? "Forza" : "Force"}
          </Button>
        )}
      </div>
      {auto && dynamicArpu && (
        <div className="assump__hint">
          {lang === "it"
            ? `Calcolato live: MRR ÷ clienti attivi. Cambia per modificare.`
            : `Computed live: MRR ÷ active customers. Edit to change.`}
        </div>
      )}
    </div>
  );
}

// ─── InputsPanel — right-side drawer for Model + Scenarios assumptions ─────
// Backdrop click + Escape close are wired by the caller (Dashboard) via the
// existing useEscapeClose hook on inputsPanelOpen. This component only renders
// the JSX when `open`. Setters are derived from `setState` to keep the prop
// surface narrow (3 fns instead of 5+).
function InputsPanel({ open, onClose, lang, state, setState, dynamicArpu }) {
  if (!open) return null;
  const setTop = (patch) => setState(s => ({ ...s, ...patch }));
  const setScenarioField = (which, patch) => setState(s => {
    const sc = s.scenarios || {};
    return { ...s, scenarios: { ...sc, [which]: { ...(sc[which] || {}), ...patch } } };
  });
  return (
    // Phase 3b — Drawer chrome (backdrop, focus trap, escape, X close,
    // animated slide-in) provided by the v2 primitive. Inner content keeps
    // its legacy .inputs-panel__body / __group / __group-title classes for
    // the existing CSS rules in legacy.css.
    // dismissOnOutsideClick preserves the original behavior (click backdrop
    // closes); the Drawer's escape handler matches the legacy useEscapeClose
    // hook this component previously expected from outside.
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      width="md"
      title={lang === "it" ? "Inputs e assunzioni" : "Inputs & assumptions"}
      dismissOnOutsideClick
    >
      <div className="inputs-panel__body">
        {/* Phase D — groups migrated to v2 <Section>. The Assump / AvgBasketInput
            children still use bespoke .assump markup (horizontal label-on-left
            layout), which is appropriate for compact form rows inside a drawer
            and doesn't map cleanly to v2 Field (which is vertical-stack). */}
        <Section title={lang === "it" ? "Modello" : "Model"}>
          <AvgBasketInput
            label={lang === "it" ? "Ticket medio (€/mese)" : "Avg basket (€/mo)"}
            value={state.avgBasket}
            auto={state.avgBasketAuto !== false}
            dynamicArpu={dynamicArpu}
            onChange={(v) => setTop({ avgBasket: v, avgBasketAuto: false })}
            onReset={() => setTop({ avgBasketAuto: true })}
            onForce={(v) => setTop({ avgBasket: v, avgBasketAuto: false })}
            lang={lang}
          />
          <Assump label={lang === "it" ? "Target/AE (€/mese)" : "Target/AE (€/mo)"} value={state.aeMonthlyTarget} onChange={(v)=>setTop({aeMonthlyTarget:v})} prefix="€" />
          <Assump label={lang === "it" ? "Demo AE/giorno" : "AE demos/day"} value={state.aeDemosPerDay} onChange={(v)=>setTop({aeDemosPerDay:v})} />
          <Assump label={lang === "it" ? "AE close rate %" : "AE close rate %"} value={state.aeCloseRate} onChange={(v)=>setTop({aeCloseRate:v})} suffix="%" />
          <Assump label={lang === "it" ? "Chiamate SDR/giorno" : "SDR calls/day"} value={state.sdrCallsPerDay} onChange={(v)=>setTop({sdrCallsPerDay:v})} />
          <Assump label={lang === "it" ? "% chiamate→demo" : "% calls→demo"} value={state.sdrToDemoRate} onChange={(v)=>setTop({sdrToDemoRate:v})} suffix="%" />
        </Section>
        <Section title={lang === "it" ? "Scenari (Target Dic 2026)" : "Scenarios (Dec 2026 target)"}>
          <Assump label="🚀 Ottimistico" value={state.scenarios?.optimistic?.eomTarget || 0} onChange={(v)=>setScenarioField("optimistic", {eomTarget: v})} prefix="€" />
          <Assump label="😟 Pessimistico" value={state.scenarios?.pessimistic?.eomTarget || 0} onChange={(v)=>setScenarioField("pessimistic", {eomTarget: v})} prefix="€" />
          <div className="inputs-panel__hint">
            {lang === "it" ? "Cambiando un valore qui, tutti i mesi futuri si riscalano. Lo scenario Base è quello modificato dalla tabella sopra." : "Editing here rescales all future months. Base scenario is edited via the table above."}
          </div>
        </Section>
      </div>{/* /inputs-panel__body */}
    </Drawer>
  );
}

// ─── SoundPickerModal — preset list + duration + microphone recorder ───────
// Recordings stored in localStorage as base64 (small). Hard cleanup ensures the
// mic stream is released even if the user closes mid-recording or an error
// boundary triggers — without that, the browser shows a permanent recording
// indicator until tab close.
function SoundPickerModal({ lang, notifSound, setNotifSound, notifDuration, setNotifDuration, hasCustomAudio, setHasCustomAudio, onClose, title, confirmLabel }) {
  const Button = window.Button;
  const useEscapeClose = window.useEscapeClose;
  const useFocusTrap = window.useFocusTrap;
  // Preset/duration tables + audio engine live in app.jsx (also consumed by the
  // notification bell + settings). Read at render time — by then app.jsx has
  // loaded and assigned them to window.
  const NOTIF_SOUND_PRESETS = window.NOTIF_SOUND_PRESETS;
  const NOTIF_DURATIONS = window.NOTIF_DURATIONS;
  const playNotifSound = window.playNotifSound;

  const [recording, setRecording] = _useState_ip(false);
  const [recError, setRecError] = _useState_ip(null);
  const recorderRef = _useRef_ip(null);
  const streamRef = _useRef_ip(null);
  const autoStopTimerRef = _useRef_ip(null);
  const chunksRef = _useRef_ip([]);
  const dialogRef = _useRef_ip(null);

  // Escape parity with backdrop click (shared hook).
  useEscapeClose(true, onClose);
  // B5: focus trap + restore-on-close.
  useFocusTrap(dialogRef, true);

  // Hard cleanup: if the modal unmounts mid-recording (user closes, tab switches, error
  // boundary triggers), we MUST release the mic — otherwise the browser shows a permanent
  // recording indicator and the stream leaks until tab close.
  _useEffect_ip(() => {
    return () => {
      try {
        if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
        if (recorderRef.current?.state === "recording") recorderRef.current.stop();
        if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      } catch {}
      recorderRef.current = null;
      streamRef.current = null;
    };
  }, []);

  const startRecording = async () => {
    setRecError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            localStorage.setItem("forecast_notif_custom_audio", reader.result);
            setHasCustomAudio(true);
          } catch (e) {
            setRecError(lang === "it" ? "Registrazione troppo grande per essere salvata. Riprova più corto." : "Recording too large to save. Try a shorter one.");
          }
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      // Auto-stop after 4s max
      autoStopTimerRef.current = setTimeout(() => {
        if (recorder.state === "recording") { recorder.stop(); setRecording(false); }
        autoStopTimerRef.current = null;
      }, 4000);
    } catch (e) {
      setRecError(lang === "it" ? "Impossibile accedere al microfono — controlla i permessi." : "Microphone access denied — check permissions.");
    }
  };
  const stopRecording = () => {
    if (autoStopTimerRef.current) { clearTimeout(autoStopTimerRef.current); autoStopTimerRef.current = null; }
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
      setRecording(false);
    }
  };
  const deleteCustom = () => {
    try { localStorage.removeItem("forecast_notif_custom_audio"); } catch {}
    setHasCustomAudio(false);
    if (notifSound === "custom") setNotifSound("ding-dong");
  };

  // Phase 3b — v2 Modal wrapper. variant="alert" renders only one action
  // button (the confirm), which doubles as the close button — matches the
  // legacy "Close" pattern at the bottom. Lang prop drives the localized
  // default ("Chiudi" / "Close"); the explicit `confirmLabel` override is
  // honored if the caller still passes one.
  return (
    <V2Modal
      open
      onClose={onClose}
      onConfirm={onClose}
      title={title}
      message={lang === "it"
        ? "Suono per nuove Closed Won. Click 'Prova' per ascoltare."
        : "Sound for new Closed Won. Click 'Preview' to hear it."}
      variant="alert"
      size="md"
      lang={lang}
      confirmLabel={confirmLabel || (lang === "it" ? "Chiudi" : "Close")}
    >
        <div className="sound-picker">
          {Object.entries(NOTIF_SOUND_PRESETS).map(([key, p]) => (
            <label key={key} className={`sound-picker__item ${notifSound === key ? "sound-picker__item--on" : ""}`}>
              <input type="radio" name="notif-sound" value={key}
                checked={notifSound === key}
                onChange={() => setNotifSound(key)} />
              <span className="sound-picker__info">
                <span className="sound-picker__label">{p.label[lang] || p.label.en}</span>
                <span className="sound-picker__desc">{p.desc[lang] || p.desc.en}</span>
              </span>
              <Button variant="ghost" size="sm" onClick={() => playNotifSound(key, notifDuration)}>
                ▶ {lang === "it" ? "Prova" : "Preview"}
              </Button>
            </label>
          ))}

          {/* Custom recording slot */}
          <label className={`sound-picker__item sound-picker__item--rec ${notifSound === "custom" ? "sound-picker__item--on" : ""}`}>
            <input type="radio" name="notif-sound" value="custom"
              checked={notifSound === "custom"}
              disabled={!hasCustomAudio}
              onChange={() => setNotifSound("custom")} />
            <span className="sound-picker__info">
              <span className="sound-picker__label">🎙 {lang === "it" ? "Registra il tuo" : "Record your own"}</span>
              <span className="sound-picker__desc">
                {recError ? <span className="sound-picker__err">{recError}</span>
                  : recording ? (lang === "it" ? "Registrazione in corso… (max 4s)" : "Recording… (max 4s)")
                  : hasCustomAudio ? (lang === "it" ? "Registrazione salvata. Sostituisci o cancella sotto." : "Recording saved. Replace or delete below.")
                  : (lang === "it" ? "Registra fino a 4 secondi dal microfono" : "Record up to 4 seconds from your mic")}
              </span>
            </span>
            <span className="sound-picker__rec-actions">
              {!recording && <Button variant="ghost" size="sm" onClick={startRecording}>● Rec</Button>}
              {recording && <Button variant="danger" size="sm" onClick={stopRecording}>■ Stop</Button>}
              {hasCustomAudio && !recording && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => playNotifSound("custom")}>▶</Button>
                  <Button variant="ghost" size="sm" onClick={deleteCustom}>🗑</Button>
                </>
              )}
            </span>
          </label>

          <label className={`sound-picker__item ${notifSound === "off" ? "sound-picker__item--on" : ""}`}>
            <input type="radio" name="notif-sound" value="off"
              checked={notifSound === "off"}
              onChange={() => setNotifSound("off")} />
            <span className="sound-picker__info">
              <span className="sound-picker__label">🔇 {lang === "it" ? "Silenzioso" : "Muted"}</span>
              <span className="sound-picker__desc">{lang === "it" ? "Nessun suono alla CW" : "No sound on CW"}</span>
            </span>
          </label>
        </div>

        {/* Duration controls */}
        <div className="sound-duration">
          <span className="sound-duration__lbl">{lang === "it" ? "Durata" : "Duration"}</span>
          {Object.entries(NOTIF_DURATIONS).map(([key, d]) => (
            <button key={key}
              className={`sound-duration__btn ${notifDuration === key ? "on" : ""}`}
              onClick={() => setNotifDuration(key)}>
              {d.label[lang] || d.label.en}
            </button>
          ))}
        </div>

        {/* Phase 3b — explicit Close button removed. v2 Modal (variant="alert")
            renders the confirm button as the only action; onConfirm is wired
            to onClose above. */}
    </V2Modal>
  );
}

// ─── Phase D: React.memo wrappers ──────────────────────────────────────────
// SoundPickerModal: takes narrow stable props (notifSound, lang, etc.);
// memo helps when modal is open and Dashboard re-renders for unrelated
// reasons. InputsPanel: when open=false, returns null (no work either way);
// when open=true, its props depend on `state` (avgBasket, scenarios, aes)
// which change every edit — memo cost ≈ memo benefit at that point. Skipped.
const SoundPickerModalMemo = React.memo(SoundPickerModal);

// ─── ESM exports (Phase 4 sub-step 6) ──────────────────────────────────────
export default InputsPanel;
export {
  SoundPickerModalMemo as SoundPickerModal,
  Assump,
  AvgBasketInput,
};
